import { Button } from "@/components/design-system/button";
import { Input } from "@/components/design-system/input";
import Modal from "@/components/design-system/Modal";
import PreviewFileModal from "@/components/design-system/PreviewFileModal";
import { AssignmentFormData } from "@/types/assignment.model";
import { getFileConfig } from "@/utils/constants";
import { Play } from "lucide-react";
import React, { useEffect, useState } from "react";

interface ViewAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: AssignmentFormData | null;
}

const ViewAssignmentModal: React.FC<ViewAssignmentModalProps> = ({
  isOpen,
  onClose,
  assignment,
}) => {
  if (!assignment) return null;

  const [previewModalOpen, setPreviewModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<{
    file_type: string;
    url: string;
    id: string;
  } | null>(null);
  const [fileType, setFileType] = useState<
    "video" | "audio" | "pdf" | "docx" | "unknown"
  >("unknown");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (assignment?.uploads && assignment.uploads.length > 0) {
      setLoading(true);
      setError(null);

      try {
        // Get file extension from URL
        const extension =
          (assignment.uploads[0].url as string)
            .split(".")
            .pop()
            ?.toLowerCase() || "";

        // Determine file type based on extension
        if (["mp4", "webm", "ogg", "mov"].includes(extension)) {
          setFileType("video");
        } else if (["mp3", "wav", "ogg", "m4a"].includes(extension)) {
          setFileType("audio");
        } else if (extension === "pdf") {
          setFileType("pdf");
        } else if (["doc", "docx"].includes(extension)) {
          setFileType("docx");
        } else {
          setFileType("unknown");
          setError(`Unsupported file type: ${extension}`);
        }
      } catch (err) {
        console.error("Error determining file type:", err);
        setError("Error determining file type");
      } finally {
        setLoading(false);
      }
    }
  }, [assignment?.uploads]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="View Assignment"
      width="896px"
      contentClassName="p-0"
    >
      <div className="p-6 flex flex-col  max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {/* Assignment name */}
          <div>
            <Input label="Assignment name" value={assignment.name} disabled />
          </div>

          {/* Select Grade */}
          <div>
            <Input
              label="Select Grade"
              value={
                typeof assignment.grade === "object"
                  ? assignment.grade.name
                  : (assignment.grade as string)
              }
              disabled
            />
          </div>

          {/* Students */}
          <div>
            <Input
              label="Students"
              value={assignment.students.length}
              disabled
            />
          </div>
        </div>

        <div className="w-full h-px bg-[#E2E8F0] mt-[32px] mb-[24px]"></div>

        {/* File Uploaded Section */}
        {/* {assignment.attachments && assignment.attachments.length > 0 && ( */}
        <div className="mb-8">
          <h2 className="text-[#1F1F1F] text-[20px] font-bold mb-6">
            File Upload
          </h2>
          <div className="flex flex-row gap-2">
            {assignment.uploads?.map((file, index) => {
              // Create a properly typed file object
              const fileObj =
                typeof file === "string"
                  ? { file_type: "", url: file, id: `file-${index}` }
                  : {
                      file_type: (file as any).file_type || "",
                      url: (file as any).url || "",
                      id: (file as any).id || `file-${index}`,
                    };

              return (
                <div key={index} className="flex items-center">
                  <button
                    onClick={() => {
                      setSelectedFile(fileObj);
                      setPreviewModalOpen(true);
                    }}
                    className="flex items-center justify-between w-[200px] gap-2 text-[#5C88C5] py-2 px-4 border border-[#5C88C5] rounded-xl"
                  >
                    <div className="flex gap-1">
                      {getFileConfig(fileObj).icon}

                      <span className="text-sm truncate max-w-[100px]">
                        {getFileConfig(fileObj).fileType}
                      </span>
                    </div>
                    <Play className="w-6 h-6 text-[#5C88C5]" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="my-4">
            <p className="text-base font-medium text-[#2D3748] mb-2">
              Write a note
            </p>
            <textarea
              className="w-full border border-gray-200 rounded-2xl p-3 min-h-[100px] text-[14px] text-[#1F1F1F] font-[500] placeholder:text-[#A0AEC0] disabled:text-[#A0AEC0] focus:outline-none"
              placeholder="Enter a note"
              value={assignment?.notes}
              disabled
            ></textarea>
          </div>
        </div>
        {/* )} */}
      </div>

      {/* Footer with close button */}
      <div className="flex justify-end border-t border-gray-200 p-4">
        <Button
          type="button"
          variant="primary-outline"
          className="w-[120px] h-[48px]"
          onClick={onClose}
        >
          Close
        </Button>
      </div>

      <PreviewFileModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={selectedFile?.url || ""}
        fileType={selectedFile?.file_type || ""}
      />
    </Modal>
  );
};

export default ViewAssignmentModal;
